import { Button, Collapse, Input, Modal, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AiFillCaretDown, AiOutlineClose, AiOutlineCloseCircle } from 'react-icons/ai';
import { omitNonLayerProperties, useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import FileManagerService from '../../../services/FileManager/fileManager.service';
import { onSocketEvent } from '../../../services/socket.service';
import { usePermissionPrincipals } from '../../../hooks/usePermissionPrincipals';
import Notification from '../../../shared/components/Notification';
import CustomWarning from '../../../shared/components/CustomWarning';
import FolderSelectorTree from '../../../shared/components/FolderSelectorTree';
import { PermissionsPicker, splitPermissionValues } from '../../../shared/components/PermissionsPicker';
import { SelectedFolder } from '../../../types/FileManagerTypes';
import { NotificationTypes } from '../../../enums/notificationTypes';
import { ROOT_DISPLAY_NAME } from '../../FileManager/constants';
import MapSelectionModal, { NO_MAP } from './ViewAttributeTable/components/MapSelectionModal';
import './SaveLayerModal.scss';
import '../MapViewStepComponents/MapViewSteps.scss';

interface SaveLayerModalProps {
  saveLayerModalVisible: boolean;
  setSaveLayerModalVisible: (value: boolean) => void;
  onSaved?: () => void;
  /** Map the user is currently in — shown first ("Current") in the map step. */
  currentMapId?: number | string;
}

type Step = 'choose' | 'new' | 'existing' | 'map';
type FileFormat = 'shapefile' | 'geojson' | 'kml';

// Maps Layer Properties' sourceFormat enum value to the file-type the
// uploadFeaturesToFolder endpoint understands. The Figma flow doesn't
// expose this — we silently default based on the layer's origin.
const defaultFormatFor = (sourceFormat?: string): FileFormat => {
  const f = (sourceFormat ?? '').toLowerCase();
  if (f.includes('shap')) return 'shapefile';
  if (f.includes('kml')) return 'kml';
  return 'geojson';
};

const SaveLayerModal: React.FC<SaveLayerModalProps> = ({ saveLayerModalVisible, setSaveLayerModalVisible, onSaved, currentMapId }) => {
  const layerProps = useLayerProperitesContext();
  const { createFilemanagerFolder, uploadFeaturesToFolder, getAllMaps, createLayerFromFeatures } = MapPortalService();
  const { getFileMapping } = FileManagerService();

  const { users, groups, isLoading: loadingPrincipals } = usePermissionPrincipals(saveLayerModalVisible);
  const { data: fileMapping, isLoading: loadingFileMapping } = useQuery(getFileMapping(saveLayerModalVisible));
  const rootFolderId = fileMapping?.rootFolderId;
  const { mutateAsync: createFolderAsync } = useMutation(createFilemanagerFolder());
  const { mutateAsync: uploadFeaturesAsync } = useMutation(uploadFeaturesToFolder({ showProgressToast: false }));
  const { mutateAsync: createLayerFromFeaturesAsync, isPending: addingToMap } = useMutation(createLayerFromFeatures());
  const { data: allMapsData, isLoading: loadingMaps } = useQuery({ ...getAllMaps(), enabled: saveLayerModalVisible });

  const allMaps: any[] = Array.isArray(allMapsData) ? allMapsData : (allMapsData?.maps ?? []);

  // After the file is written, the wizard moves to the map step; this holds the
  // folder it landed in so "add to map" can create the layer from it.
  const [savedFolderId, setSavedFolderId] = useState<string | undefined>();
  const [mapChoice, setMapChoice] = useState<number | undefined>();

  const [step, setStep] = useState<Step>('choose');
  const [locationFolderId, setLocationFolderId] = useState<string | undefined>();
  const [existingFolderId, setExistingFolderId] = useState<string | undefined>();
  const [newFolderName, setNewFolderName] = useState('');
  const [folderSelectorOpen, setFolderSelectorOpen] = useState(false);
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [layerName, setLayerName] = useState('');
  const [permissionValues, setPermissionValues] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  // Covers folder creation, the queued export and the wait for its completion event.
  const [saving, setSaving] = useState(false);

  // Reset the wizard whenever the modal is freshly opened so an aborted
  // previous save doesn't leak its values into the next attempt.
  useEffect(() => {
    if (saveLayerModalVisible) {
      setStep('choose');
      setLocationFolderId(undefined);
      setExistingFolderId(undefined);
      setNewFolderName('');
      setFolderSelectorOpen(false);
      setSelectedFolderName('');
      setLayerName(layerProps?.layerTitle ? `${layerProps.layerTitle} Copy` : '');
      setPermissionValues([]);
      setErrors({});
      setConfirmCloseOpen(false);
      setSavedFolderId(undefined);
      setMapChoice(undefined);
    }
  }, [saveLayerModalVisible, layerProps?.layerTitle]);

  const openFolderSelector = () => setFolderSelectorOpen(true);
  const closeFolderSelector = () => setFolderSelectorOpen(false);
  const handleChooseNew = () => setStep('new');
  const handleChooseExisting = () => setStep('existing');
  const handleFolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setNewFolderName(e.target.value);
  const handleLayerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setLayerName(e.target.value);
  const handleCancelClose = () => setConfirmCloseOpen(false);
  const renderExpandIcon = () => <AiFillCaretDown className="data-portal__chevron" />;

  const handleFolderSelect = (folder: SelectedFolder) => {
    if (step === 'new') setLocationFolderId(folder.id);
    else setExistingFolderId(folder.id);
    setSelectedFolderName(folder.name);
    setErrors((prev) => ({ ...prev, location: '', folder: '' }));
  };

  // Root is the default location until the user picks a folder.
  const destinationFolderId = (step === 'new' ? locationFolderId : existingFolderId) || rootFolderId;
  const destinationLabel = selectedFolderName || (rootFolderId ? ROOT_DISPLAY_NAME : loadingFileMapping ? 'Loading…' : '');

  const hasDirtyForm = useMemo(() => {
    if (step === 'choose') return false;
    return !!layerName.trim() || !!newFolderName.trim() || !!locationFolderId || !!existingFolderId || permissionValues.length > 0;
  }, [step, layerName, newFolderName, locationFolderId, existingFolderId, permissionValues]);

  const requestClose = () => {
    if (saving) return;
    // On the map step the file is already saved — closing just skips adding to a
    // map, so don't warn about losing work.
    if (step === 'map') {
      setSaveLayerModalVisible(false);
      onSaved?.();
      return;
    }
    if (hasDirtyForm) {
      setConfirmCloseOpen(true);
    } else {
      setSaveLayerModalVisible(false);
    }
  };

  const forceClose = () => {
    setConfirmCloseOpen(false);
    setSaveLayerModalVisible(false);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (step === 'new') {
      if (!destinationFolderId) e.location = 'Location is required';
      if (!newFolderName.trim()) e.folderName = 'Folder name is required';
    }
    if (step === 'existing') {
      if (!destinationFolderId) e.folder = 'Folder is required';
    }
    if (!layerName.trim()) e.fileName = 'File name is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      let targetFolderId = destinationFolderId;

      // AC-02 "Save in New Folder": create the folder under the chosen
      // parent location, then write into it.
      if (step === 'new') {
        const created = await createFolderAsync({
          folderName: newFolderName.trim(),
          currentDirectoryId: destinationFolderId!,
        });
        targetFolderId = created?.folder?.folder_id ?? created?.folderId ?? created?.id ?? created?.folder?.id;
        if (!targetFolderId) {
          throw new Error('Folder creation succeeded but no folder id returned');
        }
      }

      // AC-05: pass only selected feature gids when an attribute-table
      // selection is active; otherwise leave empty so the backend exports
      // the whole dataset.
      const selectedFeatureIds: string[] = (layerProps?.selectedFeatureIds ?? []).map(String);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedFeatureIds: _drop, ...propertiesToSend } = omitNonLayerProperties(layerProps) as any;

      // Async save: the export now runs on the worker, so we pass the chosen
      // permissions in the payload and the queue completed-handler applies them
      // to each file it creates (the files don't exist yet to set them here).
      const { userValues, groupValues } = splitPermissionValues(permissionValues);

      const fileType = defaultFormatFor(layerProps?.sourceFormat);
      let stopListening: (() => void) | undefined;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let queuedJobId: string | number | undefined;
      const datasetReady = new Promise<any>((resolve, reject) => {
        stopListening = onSocketEvent<any>('save-features:completed', (event) => {
          if (queuedJobId && String(event?.jobId) !== String(queuedJobId)) return;
          if (event?.folderId !== targetFolderId) return;
          if (event?.status === 'failed') {
            stopListening?.();
            reject(new Error(event.error || 'Dataset creation failed'));
            return;
          }
          stopListening?.();
          resolve(event);
        });
      });

      try {
        const queued = await uploadFeaturesAsync({
          payload: {
            fileName: layerName.trim(),
            fileType,
            featureIds: selectedFeatureIds,
            ...(userValues.length || groupValues.length ? { permissions: { userValues, groupValues } } : {}),
            properties: {
              ...propertiesToSend,
              folderId: targetFolderId,
            },
          },
        });
        queuedJobId = queued?.jobId;
        const completed = await Promise.race([
          datasetReady,
          new Promise((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error('Timed out waiting for saved dataset')), 10 * 60 * 1000);
          }),
        ]);
        if (!completed?.datasetId) throw new Error('Saved dataset did not return a dataset id');
      } finally {
        stopListening?.();
        clearTimeout(timeoutId);
      }

      Notification({
        message: selectedFeatureIds.length > 0 ? 'Selected features saved to file manager' : 'Layer saved to file manager',
        type: NotificationTypes.SUCCESS,
      });
      // #4 — after the file lands in the folder, offer to add it to a map as a
      // layer. Defaults to "Don't add to a map" = file only.
      setSavedFolderId(targetFolderId);
      setMapChoice(currentMapId != null ? Number(currentMapId) : NO_MAP);
      setStep('map');
    } catch (err: any) {
      // Mutation hooks toast their own errors; just log here.
      console.error('Save Layer As failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // #4 — optionally add the just-saved dataset to a map as a layer.
  const handleAddToMap = async () => {
    if (addingToMap) return;
    if (mapChoice == null || mapChoice === NO_MAP) {
      setSaveLayerModalVisible(false);
      onSaved?.();
      return;
    }
    try {
      // The dataset/revision was created by the completed save-features job.
      // Save the selected features to the map through the file-manager
      // save-features endpoint, not the S3 save-layer endpoint.
      const featureIds = (layerProps?.selectedFeatureIds ?? []).map(String);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedFeatureIds: _drop, ...propertiesToSend } = omitNonLayerProperties(layerProps) as any;
      await createLayerFromFeaturesAsync({
        payload: {
          mapId: mapChoice,
          layerName: layerName.trim(),
          ...(featureIds.length ? { featureIds } : {}),
          properties: { ...propertiesToSend, folderId: savedFolderId },
        },
      });
      Notification({ message: 'Layer added to map', type: NotificationTypes.SUCCESS });
      setSaveLayerModalVisible(false);
      onSaved?.();
    } catch (err) {
      console.error('Add to map failed:', err);
    }
  };

  const renderHeader = (title: string, permissionSelector: boolean) => (
    <div className={`save-layer-modal${permissionSelector ? '__permissions' : '__header'}`}>
      <h2 className="save-layer-modal__title">{title}</h2>
      <button className="save-layer-modal__close" onClick={requestClose} disabled={saving} aria-label="Close">
        <AiOutlineClose />
      </button>
    </div>
  );

  const renderChoose = () => (
    <>
      {renderHeader('Save Layer As', false)}
      <div className="save-layer-modal__body">
        <div className="save-layer-modal__choices">
          <button className="save-layer-modal__card" onClick={handleChooseNew}>
            <img src={'/assets/save_new_folder_icon.svg'} />
            <span className="save-layer-modal__card-label">Save to New Folder</span>
          </button>
          <button className="save-layer-modal__card" onClick={handleChooseExisting}>
            <img src={'/assets/save_existing_folder_icon.svg'} />
            <span className="save-layer-modal__card-label">Save to Existing Folder</span>
          </button>
        </div>
      </div>
    </>
  );

  const renderErrorLine = (key: string) =>
    errors[key] ? (
      <div className="save-layer-modal__error">
        <AiOutlineCloseCircle />
        <span>{errors[key]}</span>
      </div>
    ) : null;

  const permissionsBlock = (
    <Collapse
      defaultActiveKey={['1']}
      className="data-portal__collapse save-layer-modal__collapse"
      expandIconPosition="end"
      expandIcon={renderExpandIcon}
      items={[
        {
          key: '1',
          label: 'FILE PERMISSIONS',
          children: (
            <PermissionsPicker
              users={users}
              groups={groups}
              selectedValues={permissionValues}
              onChange={setPermissionValues}
              isLoading={loadingPrincipals}
            />
          ),
        },
      ]}
    />
  );

  const renderNewForm = () => (
    <>
      {renderHeader('Save File to a New Folder', true)}
      <div className="save-layer-modal__body">
        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Location</label>
          <Button
            className={`save-layer-modal__location-btn${errors.location ? ' save-layer-modal__location-btn--error' : ''}`}
            onClick={openFolderSelector}
          >
            <p className="save-layer-modal__location-text">{destinationLabel || 'Location'}</p>
            <AiFillCaretDown className="save-layer-modal__chevron" />
          </Button>
          {renderErrorLine('location')}
        </div>

        {permissionsBlock}

        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Folder Name</label>
          <Input value={newFolderName} onChange={handleFolderNameChange} status={errors.folderName ? 'error' : undefined} />
          {renderErrorLine('folderName')}
        </div>

        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">File Name</label>
          <Input
            value={layerName}
            onChange={handleLayerNameChange}
            placeholder="e.g. land_use_2026"
            status={errors.fileName ? 'error' : undefined}
          />
          {renderErrorLine('fileName')}
        </div>

        <div className="save-layer-modal__actions">
          <Button className="save-layer-modal__add-btn" loading={saving} disabled={!destinationFolderId} onClick={handleSave}>
            Add
          </Button>
        </div>
      </div>
    </>
  );

  const renderExistingForm = () => (
    <>
      {renderHeader('Save File to Existing Folder', true)}
      <div className="save-layer-modal__body">
        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">Folder</label>
          <Button
            className={`save-layer-modal__location-btn${errors.folder ? ' save-layer-modal__location-btn--error' : ''}`}
            onClick={openFolderSelector}
          >
            <p className="save-layer-modal__location-text">{destinationLabel || 'Folder'}</p>
            <AiFillCaretDown className="save-layer-modal__chevron" />
          </Button>
          {renderErrorLine('folder')}
        </div>

        {permissionsBlock}

        <div className="save-layer-modal__field">
          <label className="save-layer-modal__label">File Name</label>
          <Input
            value={layerName}
            onChange={handleLayerNameChange}
            placeholder="e.g. land_use_2026"
            status={errors.fileName ? 'error' : undefined}
          />
          {renderErrorLine('fileName')}
        </div>

        <div className="save-layer-modal__actions">
          <Button className="save-layer-modal__add-btn" loading={saving} disabled={!destinationFolderId} onClick={handleSave}>
            Add
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Modal
        className="save-layer-modal"
        open={saveLayerModalVisible && step !== 'map'}
        onCancel={requestClose}
        keyboard={!saving}
        centered
        maskClosable={false}
        closable={false}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Spin spinning={saving} tip="Saving layer…">
          {step === 'choose' && renderChoose()}
          {step === 'new' && renderNewForm()}
          {step === 'existing' && renderExistingForm()}
        </Spin>
      </Modal>

      <MapSelectionModal
        visible={saveLayerModalVisible && step === 'map'}
        onCancel={requestClose}
        loadingMaps={loadingMaps}
        allMaps={allMaps}
        selectedMap={mapChoice ?? null}
        setSelectedMap={setMapChoice}
        currentMapId={currentMapId}
        allowNoMap
        onNext={handleAddToMap}
      />

      <FolderSelectorTree
        open={folderSelectorOpen}
        onClose={closeFolderSelector}
        onSelect={handleFolderSelect}
        variant="light"
        primaryLabel="SELECT"
        initialFolderId={destinationFolderId}
      />

      <CustomWarning
        open={confirmCloseOpen}
        onCancel={handleCancelClose}
        onConfirm={forceClose}
        message={
          <>
            Your file will not be saved!
            <br />
            Close the &lsquo;Save As&rsquo; window?
          </>
        }
      />
    </>
  );
};

export default SaveLayerModal;
